import React, { useEffect, useState, useCallback } from 'react';
import BeePlugin from '@mailupinc/bee-plugin';
import Text from 'antd/lib/typography/Text';
import { Link, Redirect, useParams } from 'react-router-dom';
import {
  Alert,
  Col,
  Divider,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popover,
  Row,
  Space,
  Typography
} from 'antd';
import axiosConfig, { api } from '../utils/axiosConfig';
import { Form } from 'antd';
import { Auth } from 'aws-amplify';
import { ResponseModel } from '../models/fetchingData';
import { Template } from '../interfaces/backend/Templates';
import { EmailComponentProps } from '../models/emailComponents';
import { CardsListRenderProps } from '../models/CardsListRender';
import { Channels } from '../components/common/channelsEnum';
import { Components } from '../components/common/componentsEnum';
import { URLMap, beeEditorOnSaveHandler } from './editor';
import BeeSaveComponentModal from './BeeSaveComponentModal';
import BeeOverwriteModal from './BeeOverwriteModal';
import { Layout } from 'antd';
import {
  CloseCircleOutlined,
  SendOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  InfoCircleTwoTone,
  WarningFilled,
  EyeOutlined
} from '@ant-design/icons';
import { readWebsiteVariable } from '../utils/readWebsiteVariable';
import Loading from '../components/loading';
import styled from 'styled-components';
import { DKIM_STATUS } from '../interfaces/api/Senders';
import { EmailerSendersGETResponse } from '../interfaces/backend/EmailerSendersGETResponse';
import BatchingInfo from '../components/BatchingInfo';
import { Notification } from '../interfaces/backend/Notifications';
import TextArea from 'antd/es/input/TextArea';
import {
  getAllTags,
  liquidizeTemplateWithMergeTags
} from '../components/notifications/LiquidizedPreview';
import EmailPreviewIframe from '../components/notifications/tabs/email/EmailPreviewIframe';
import { Liquid } from 'liquidjs';

const liquid = new Liquid();
const FormItem = Form.Item;
interface Metadata {
  name: string;
  componentId: string;
}
declare global {
  interface Window {
    beeSaveResolver: (metadata: Metadata) => Promise<void>;
    beeSaveRejector: () => Promise<void>;
    beeHandleMetadata: Metadata;
    beeSavedRowsComponentId: string[];
  }
}

const BeeContainer = styled.div<{
  leftSidebarOpen: boolean;
}>`
  position: absolute;
  top: 64px;
  left: ${(props) => (props.leftSidebarOpen ? '340px' : '20px')};
  right: 0;
  bottom: 0;
  transition: all 0.3s;
`;

const Sidebar = styled.div<{ leftSidebarOpen: boolean }>`
  background: #f9f9f9;
  position: absolute;
  padding: 20px 12px 12px 12px;
  top: 64px;
  bottom: 0;
  border: 1px solid #c7c7c7;
  transition: all 0.2s;
  z-index: 1;
  width: 340px;

  left: ${(props) => (props.leftSidebarOpen ? '0' : '-320px')};

  &.closed {
    padding-right: 20px;
  }

  &.closed:hover {
    left: -310px;
    cursor: pointer;
  }

  & .closeButton {
    position: absolute;
    top: -6px;
    left: 312px;
    width: 30px;
    height: 30px;
    background: #4cb9ea;
    border-radius: 50%;
    z-index: 1;
    text-align: center;
    padding-top: 7px;
    transition: all 0.2s;
    opacity: 0;
    cursor: pointer;
  }

  &.closed .closeButton {
    opacity: 1;
    transform: scaleX(-1);
  }

  &:hover .closeButton {
    opacity: 1;
  }

  .closeButton:hover {
    box-shadow: 0px 2px 6px 2px rgba(0, 0, 0, 0.1),
      inset 0 0 0px 4px rgba(0, 0, 0, 0.1);
  }
`;

export type EmailTemplateSubset = Template extends infer T
  ? T extends { channel: 'EMAIL' }
    ? T
    : never
  : never;

interface BeeToken {
  token: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
}
interface BeeExternalContentURLs {
  name: string;
  value: string;
}

const BeeEmailEditor: React.FC<CardsListRenderProps> = ({
  channel
}): JSX.Element => {
  const { notificationId, savesRowName, templateId } =
    useParams<{
      notificationId: string;
      savesRowName: string;
      templateId: string;
    }>();
  const { accountId } = useParams<{ accountId: string }>();
  const [redirectState, changeRedirectState] = useState(false);
  const [loadingState, changeLoadingState] = useState(true);
  const [savedComponentName, changeSavedComponentName] = useState<string>();
  const [beeToken, changeBeeToken] = useState<BeeToken>();
  const [beeUser, changeBeeUser] = useState<string>();
  const [beeInstance, setBeeInstance] = useState<Bee>();
  const [form] = Form.useForm();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const [changedSinceSave, setChangedSinceSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setTemplateLoading] = useState<boolean>(true);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [HTMLOnSave, changeHTMLOnSave] = useState<string>();
  const [internalOnSave, changeInternalOnSave] = useState<string>();
  const [subject, changeSubject] = useState<string>();
  const [subjectStatus, changeSubjectStatus] = useState<boolean>(true);
  const [preview, changePreview] = useState<string>();
  const [addressFrom, changeAddressFrom] = useState<string>();
  const [addressFromStatus, changeAddressFromStatus] = useState<boolean>(true);
  const [nameFrom, changeNameFrom] = useState<string>();
  const [nameFromStatus, changeNameFromStatus] = useState<boolean>(true);
  const [senders, setSenders] = useState<EmailerSendersGETResponse>();
  const [previewing, setPreviewing] = useState(false);
  const [previewStatus, changePreviewStatus] = useState<boolean>(true);
  const [testing, setTesting] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [testingMergeTags, setTestingMergeTags] = useState<
    Record<string, unknown>
  >({});
  const [batchSize, setBatchSize] = useState<number>(1);
  const [template, setTemplate] = useState<EmailTemplateSubset>();
  const [notification, setNotification] = useState<Notification>();
  const [htmlOnTest, setHtmlOnTest] = useState<string>('');
  const [liquidResults, setLiquidResults] =
    useState<{
      result?: Template;
      errors: string[];
    }>();
  const [invalidTestingMergeTags, setInvalidTestingMergeTags] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState<string>('');

  const redirectURL: URLMap = {
    [Channels.EMAIL]: `/${accountId}/notifications/${notificationId}/notificationConfig/design`,
    [Components.EMAIL]: `/${accountId}/settings/${channel}`
  };

  interface Bee {
    save: () => void;
    send: () => void;
    start: (config: unknown, data: unknown) => void;
    load: (data: unknown) => void;
    instance: {
      togglePreview: () => void;
      reload: (data: unknown) => void;
      load: (data: unknown) => void;
    };
  }

  const startBee = useCallback(async (): Promise<void> => {
    const httpURLSelection: URLMap = {
      [Channels.EMAIL]: `/${accountId}/notifications/${notificationId}/templates/EMAIL/${templateId}`,
      [Components.EMAIL]: `/${accountId}/components/EMAIL/${templateId}`
    };

    if (beeToken && beeUser) {
      const beeEditor: Bee = new BeePlugin(beeToken.token);
      setBeeInstance(beeEditor);
      let beeRowExternalContentURLs: BeeExternalContentURLs[];
      if (channel === Channels.EMAIL) {
        beeRowExternalContentURLs = [
          {
            name: 'Components',
            value: `https://${readWebsiteVariable(
              'REACT_APP_api_domain'
            )}/${accountId}/components_public_url/EMAIL`
          }
        ];
      } else {
        beeRowExternalContentURLs = [];
      }
      const beeConfiguration = {
        uid: beeUser,
        container: 'bee-plugin-container',
        language: 'en-US',
        autosave: false,
        trackChanges: true,
        rowDisplayConditions: [
          {
            type: 'Batching',
            label: 'Repeat this row for each batched item',
            description:
              'Inside the row, you can use mergeTags like: _item.name',
            before: '{% for _item in _items %}',
            after: '{% endfor %}'
          },
          {
            type: 'Batching',
            label: 'Start repeating rows here (1/2)',
            description:
              'Repeats this and every row after until "Stop repeating..."',
            before: '{% for _item in _items %}',
            after: '{% # %}'
          },
          {
            type: 'Batching',
            label: 'Stop repeating rows here (2/2)',
            description: 'Apply to the last row you want repeated',
            before: '{% # %}',
            after: '{% endfor %}'
          },
          {
            type: 'Batching',
            label: 'Display when batch size > 1',
            description:
              'Only displays this row when there are multiple items.',
            before: '{% if _items.size > 1 %}',
            after: '{% endif %}'
          },
          {
            type: 'Batching',
            label: 'Display when batch size is 1',
            description:
              'Only displays this row when there is exactly one item.',
            before: '{% if _items.size == 1 %}',
            after: '{% endif %}'
          },
          {
            type: 'Samples',
            label: 'Loop (Repeat)',
            description: 'Replace "items" with real array from mergeTags',
            before: '{% for item in items %}',
            after: '{% endfor %}'
          },
          {
            type: 'Samples',
            label: 'If (Conditional)',
            description: 'Replace condition with real condition',
            before: '{% if param == "something" %}',
            after: '{% endif %}'
          }
        ],
        contentDialog: {
          saveRow: {
            handler: (
              resolve: () => Promise<void>,
              reject: () => Promise<void>,
              args: { metadata: Metadata }
            ): void => {
              window.beeSaveResolver = resolve;
              window.beeSaveRejector = reject;
              if (args.metadata?.componentId) {
                window.beeHandleMetadata = args.metadata;
                changeSavedComponentName(args.metadata.name);
                if (
                  !window.beeSavedRowsComponentId.includes(
                    args.metadata.componentId
                  )
                ) {
                  window.beeSavedRowsComponentId.push(
                    args.metadata.componentId
                  );
                }
                setShowOverwriteModal(true);
              } else {
                setShowSaveModal(true);
              }
            }
          }
        },
        rowsConfiguration: {
          emptyRows: true,
          defaultRows: false,
          externalContentURLs: beeRowExternalContentURLs
        },
        onTogglePreview: (isPreview: boolean): void => {
          if (!isPreview) {
            setPreviewing(false);
          }
        },
        onPreview: (isPreview: boolean): void => {
          if (!isPreview) {
            setPreviewing(false);
          }
        },
        onSave: async (jsonFile: string, htmlFile: string): Promise<void> => {
          changeHTMLOnSave(htmlFile);
          changeInternalOnSave(jsonFile);
        },
        onSend: (htmlFile: string): void => {
          setHtmlOnTest(htmlFile);
        },
        onLoad: (jsonFile: unknown) => {
          setTemplateLoading(false);
        },
        onError: (errorMessage: string): void => {
          console.log('onError ', errorMessage);
        },
        onSaveRow: async (
          rowJSON: string,
          rowHTML: string,
          pageJSON: string
        ): Promise<void> => {
          const rowJson = JSON.parse(rowJSON);
          if (
            window.beeSavedRowsComponentId.includes(
              rowJson.metadata.componentId
            )
          ) {
            const saveRowParams = {
              name: window.beeHandleMetadata.name,
              internal: rowJSON,
              internalPage: pageJSON,
              html: rowHTML
            };
            await axiosConfig(
              'patch',
              `/${accountId}/components/${Channels.EMAIL}/${rowJson.metadata.componentId}`,
              saveRowParams,
              () => {
                message.success('Row updated', 1);
              },
              (e) => {
                console.log(e);
              }
            );
          } else {
            const saveRowParams = {
              name: window.beeHandleMetadata.name,
              componentId: window.beeHandleMetadata.componentId,
              internal: rowJSON,
              internalPage: pageJSON,
              html: rowHTML
            };
            await axiosConfig(
              'post',
              `/${accountId}/components/${Channels.EMAIL}`,
              saveRowParams,
              () => {
                message.success('Row Saved', 1);
              },
              (e) => {
                console.log(e);
              }
            );
          }
        },
        onChange: (jsonFile: unknown): void => {
          setChangedSinceSave(true);
        },
        editorFonts: {
          showDefaultFonts: false,
          customFonts: [
            {
              name: 'Arial',
              fontFamily: "Arial, 'Helvetica Neue', Helvetica, sans-serif"
            },
            {
              name: 'Bitter',
              fontFamily: "'Bitter', Georgia, Times, 'Times New Roman', serif",
              url: 'https://fonts.googleapis.com/css?family=Bitter'
            },
            {
              name: 'Courier',
              fontFamily:
                "'Courier New', Courier, 'Lucida Sans Typewriter', 'Lucida Typewriter', monospace"
            },
            {
              name: 'Droid Serif',
              fontFamily:
                "'Droid Serif', Georgia, Times, 'Times New Roman', serif",
              url: 'https://fonts.googleapis.com/css?family=Droid+Serif'
            },
            {
              name: 'Georgia',
              fontFamily: "Georgia, Times, 'Times New Roman', serif"
            },
            {
              name: 'Helvetica',
              fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
            },
            {
              name: 'Lato',
              fontFamily: "'Lato', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Lato'
            },
            {
              name: 'League Spartan',
              fontFamily:
                "'League Spartan', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=League+Spartan'
            },
            {
              name: 'Lucida Sans',
              fontFamily:
                "'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Geneva, Verdana, sans-serif"
            },
            {
              name: 'Montserrat',
              fontFamily:
                "'Montserrat', 'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', Tahoma, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Montserrat'
            },
            {
              name: 'Open Sans',
              fontFamily:
                "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Open+Sans'
            },
            {
              name: 'Roboto',
              fontFamily: "'Roboto', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Roboto'
            },
            {
              name: 'Sora',
              fontFamily: "'Sora', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Sora'
            },
            {
              name: 'Source Sans Pro',
              fontFamily:
                "'Source Sans Pro', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Source+Sans+Pro'
            },
            {
              name: 'Tahoma',
              fontFamily: 'Tahoma, Verdana, Segoe, sans-serif'
            },
            {
              name: 'Times New Roman',
              fontFamily:
                "TimesNewRoman, 'Times New Roman', Times, Beskerville, Georgia, serif"
            },
            {
              name: 'Trebuchet MS',
              fontFamily:
                "'Trebuchet MS', 'Lucida Grande', 'Lucida Sans Unicode', 'Lucida Sans', Tahoma, sans-serif"
            },
            {
              name: 'Ubuntu',
              fontFamily: "'Ubuntu', Tahoma, Verdana, Segoe, sans-serif",
              url: 'https://fonts.googleapis.com/css?family=Ubuntu'
            },
            {
              name: 'Verdana',
              fontFamily: 'Verdana, Geneva, sans-serif'
            },
            {
              name: 'Plus Jakarta Sans',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              url: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans'
            }
          ]
        }
      };

      return await axiosConfig(
        'get',
        httpURLSelection[channel],
        {},
        async (response) => {
          const res = response as ResponseModel<
            EmailTemplateSubset | EmailComponentProps
          >;
          let data;

          if (channel === Components.EMAIL) {
            data = JSON.parse((res.data as EmailComponentProps).internalPage);
          } else {
            data = JSON.parse((res.data as EmailTemplateSubset).internal);
            const template: EmailTemplateSubset =
              res.data as EmailTemplateSubset;
            changeSubject(template.subject);
            changeAddressFrom(template.senderEmail);
            changePreview(template.previewText);
            changeNameFrom(template.senderName);
            setTestingMergeTags(getAllTags([template]).mergeTagsObject);
          }

          changeLoadingState(false);

          window.beeSavedRowsComponentId = [];

          if (channel === Channels.EMAIL) {
            for (let i = 0; i < data.page.rows.length; i++) {
              if (data.page.rows[i].metadata) {
                if (data.page.rows[i].metadata.componentId) {
                  window.beeSavedRowsComponentId.push(
                    data.page.rows[i].metadata.componentId
                  );
                }
              }
            }
          }
          return await beeEditor.start(beeConfiguration, data);
        }
      );
    }
  }, [beeToken, beeUser, channel, accountId, notificationId, templateId]);

  const getBeeToken = useCallback(async (): Promise<void> => {
    if (beeUser) {
      await axiosConfig('post', `/${accountId}/bee`, {}, (response) => {
        const res = response as ResponseModel<BeeToken>;
        changeBeeToken(res.data);
      });
    }
  }, [beeUser, accountId]);
  const setBeeUserId = async (): Promise<void> => {
    await Auth.currentUserInfo().then((userInfo) => {
      setTestEmailAddress(userInfo.attributes.email);
      changeBeeUser(userInfo.attributes['custom:envIds']);
    });
  };

  useEffect(() => {
    try {
      liquid.parseAndRenderSync(subject ?? '', {});
      changeSubjectStatus(true);
    } catch {
      changeSubjectStatus(false);
    }
  }, [subject]);

  useEffect(() => {
    try {
      liquid.parseAndRenderSync(preview ?? '', {});
      changePreviewStatus(true);
    } catch {
      changePreviewStatus(false);
    }
  }, [preview]);

  useEffect(() => {
    try {
      liquid.parseAndRenderSync(addressFrom ?? '', {});
      changeAddressFromStatus(true);
    } catch {
      changeAddressFromStatus(false);
    }
  }, [addressFrom]);

  useEffect(() => {
    try {
      liquid.parseAndRenderSync(nameFrom ?? '', {});
      changeNameFromStatus(true);
    } catch {
      changeNameFromStatus(false);
    }
  }, [nameFrom]);

  useEffect(() => {
    setBeeUserId();
  }, []);

  useEffect(() => {
    getBeeToken();
  }, [getBeeToken]);
  useEffect(() => {
    startBee();
  }, [startBee]);

  useEffect(() => {
    if (showSaveModal) {
      form.setFieldsValue({ Name: '' });
    }
  }, [showSaveModal, form]);

  useEffect(() => {
    const handleSave = async () => {
      const httpURLSelection: URLMap = {
        [Channels.EMAIL]: `/${accountId}/notifications/${notificationId}/templates/EMAIL/${templateId}`,
        [Components.EMAIL]: `/${accountId}/components/EMAIL/${templateId}`
      };
      if (saving && internalOnSave && HTMLOnSave) {
        await beeEditorOnSaveHandler(
          internalOnSave,
          HTMLOnSave,
          subject,
          preview,
          addressFrom,
          nameFrom,
          httpURLSelection,
          savesRowName,
          channel,
          setSaving,
          setChangedSinceSave
        );
        setSaving(false);
        setChangedSinceSave(false);
      }
    };

    handleSave();
  }, [
    saving,
    internalOnSave,
    HTMLOnSave,
    subject,
    preview,
    addressFrom,
    nameFrom,
    savesRowName,
    accountId,
    notificationId,
    templateId,
    channel,
    setSaving,
    setChangedSinceSave
  ]);
  const matchSenderWithAllSenders = (
    senderEmail: string | undefined,
    allSenders: EmailerSendersGETResponse
  ): EmailerSendersGETResponse[number] | undefined => {
    const senderDomain = senderEmail?.split('@')[1];
    // prioritize domains, I believe that's how SES works
    let sender = allSenders
      .filter((s) => s.type === 'domain')
      .find((s) => s.sender === senderDomain);
    if (sender) return sender;

    sender = allSenders
      .filter((s) => s.type === 'email')
      .find((s) => s.sender === senderEmail);

    return sender;
  };
  useEffect(() => {
    api('GET', `/${accountId}/emailer/senders`).then((res) => {
      setSenders(res.data as EmailerSendersGETResponse);
    });
  }, [accountId]);

  // get the notification and template, to see if showing batching info is necessary:
  useEffect(() => {
    api(
      'GET',
      `/${accountId}/notifications/${notificationId}/templates/EMAIL/${templateId}`
    ).then((res) => {
      setTemplate(res.data as EmailTemplateSubset);
    });
    api('GET', `/${accountId}/notifications/${notificationId}`).then((res) => {
      setNotification((res.data as Notification[])[0]);
    });
  }, [accountId, notificationId, templateId]);

  // liquidize
  useEffect(() => {
    if (!template) return;

    const mergeTags = { ...testingMergeTags };
    const _items = [];
    for (let i = 0; i < batchSize; i++) {
      _items.push({
        ...mergeTags
      });
    }
    mergeTags._items = _items;
    const res = liquidizeTemplateWithMergeTags(
      {
        ...template,
        internal: '',
        subject: subject ?? '',
        previewText: preview,
        html: htmlOnTest
      },
      mergeTags
    );
    if (res.errors.length === 0) {
      setLiquidResults(res);
    }
  }, [template, batchSize, subject, preview, htmlOnTest, testingMergeTags]);

  const sender = matchSenderWithAllSenders(addressFrom, senders ?? []);
  const getEmailSenderAlert = (): React.ReactNode => {
    if (!sender)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              This email will be sent from our default address, because your
              FROM address is not verified on our end. Go to{' '}
              <Link to={`/${accountId}/settings/domain-verification`}>
                {`Settings->Domain Verification`}
              </Link>{' '}
              to verify your domain.
            </Typography.Paragraph>
          }
          type="error"
          style={{ marginBottom: '20px' }}
        />
      );

    if (sender?.status === DKIM_STATUS.FAILED)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              The verification process for your FROM address has failed. Please
              go to{' '}
              <Typography.Link
                href={`/${accountId}/settings/domain-verification`}
              >
                Settings/Domain Verification
              </Typography.Link>{' '}
              to retry. Until verification is successful, emails will continue
              to go out from noreply@notificationapi.com.
            </Typography.Paragraph>
          }
          type="error"
          style={{ marginBottom: '20px' }}
        />
      );

    if (sender?.status === DKIM_STATUS.PENDING)
      return (
        <Alert
          onClick={(e) => {
            e.stopPropagation();
          }}
          description={
            <Typography.Paragraph>
              Follow the instructions in{' '}
              <Link to={`/${accountId}/settings/domain-verification`}>
                Setting/Domain Verification
              </Link>{' '}
              to verify your address. Until verification is successful, emails
              will continue going out from noreply@notificationapi.com.
            </Typography.Paragraph>
          }
          type="warning"
          style={{ marginBottom: '20px' }}
        />
      );
  };

  return redirectState ? (
    <Redirect to={redirectURL[channel]} />
  ) : (
    <>
      <Layout.Header style={{ height: '64px', width: '100%' }}>
        <div>
          <Menu
            theme="dark"
            inlineCollapsed={false}
            mode="horizontal"
            selectedKeys={[]}
            disabled={saving || loading}
            style={{
              justifyContent: 'center',
              display: 'flex'
            }}
          >
            <Menu.Item
              key="save"
              disabled={testing || previewing}
              icon={saving ? <LoadingOutlined /> : <SaveOutlined />}
              onClick={async () => {
                setSaving(true);
                await beeInstance?.save();
              }}
            >
              Save
            </Menu.Item>
            <Menu.Item
              key="test"
              disabled={previewing}
              icon={<SendOutlined />}
              onClick={() => {
                setTesting(!testing);
                beeInstance?.send();
              }}
            >
              Test
            </Menu.Item>
            <Menu.Item
              key="preview"
              icon={<EyeOutlined />}
              onClick={async () => {
                beeInstance?.instance.togglePreview();
                setPreviewing(!previewing);
              }}
            >
              {previewing ? 'Exit Preview' : 'Preview'}
            </Menu.Item>
            <Menu.Item
              key="close"
              disabled={testing || previewing}
              icon={<CloseCircleOutlined />}
              onClick={() => {
                if (changedSinceSave) {
                  Modal.confirm({
                    title: 'You have unsaved changes',
                    content: 'What would you like to do?',
                    icon: <ExclamationCircleOutlined />,
                    okText: 'Continue editing',
                    cancelText: 'Exit without saving',
                    onCancel: () => {
                      changeRedirectState(true);
                    }
                  });
                } else {
                  changeRedirectState(true);
                }
              }}
            >
              Close
            </Menu.Item>
          </Menu>
        </div>
      </Layout.Header>
      {loadingState && <Loading />}
      {!loadingState && (
        <>
          <Sidebar
            leftSidebarOpen={leftSidebarOpen}
            onClick={() => {
              if (!leftSidebarOpen) {
                setLeftSidebarOpen(true);
              }
            }}
            className={`${!leftSidebarOpen ? 'closed' : ''}`}
          >
            <div
              style={{
                position: 'relative',
                width: '100%'
              }}
            >
              <div
                className={`closeButton ${!leftSidebarOpen ? 'closed' : ''}`}
                onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
              >
                <svg width="12" height="12" viewBox="0 0 22 22">
                  <path
                    d="M15.1186 20.6664L16.0829 19.7021C16.2114 19.5736 16.2757 19.4236 16.2757 19.2521C16.2757 19.0893 16.2114 18.9393 16.0829 18.8021L8.47144 11.1907L16.0829 3.57928C16.2114 3.45071 16.2757 3.30071 16.2757 3.12928C16.2757 2.96643 16.2114 2.82071 16.0829 2.69214L15.1186 1.715C14.99 1.58643 14.84 1.52214 14.6686 1.52214C14.5057 1.52214 14.3557 1.58643 14.2186 1.715L5.19287 10.7536C5.0643 10.8821 5.00001 11.0279 5.00001 11.1907C5.00001 11.3621 5.0643 11.5121 5.19287 11.6407L14.2186 20.6664C14.3557 20.795 14.5057 20.8593 14.6686 20.8593C14.84 20.8593 14.99 20.795 15.1186 20.6664Z"
                    fill="white"
                  ></path>
                </svg>
              </div>
            </div>

            <Form
              onChange={() => {
                setChangedSinceSave(true);
              }}
              layout="vertical"
            >
              <FormItem
                label={<span>Subject</span>}
                rules={[
                  {
                    required: true,
                    message: 'Please enter the email subject'
                  }
                ]}
              >
                <Input
                  value={subject}
                  status={subjectStatus ? '' : 'error'}
                  onChange={(e) => {
                    changeSubject(e.target.value);
                  }}
                />
              </FormItem>
              <FormItem
                label={
                  <div>
                    <span>Preview Text</span>
                    <Popover
                      placement="left"
                      overlayStyle={{ width: '400px' }}
                      content={
                        <div>
                          <p style={{ textAlign: 'justify' }}>
                            Preview Text is what email clients display to users
                            in the email preview. If you leave this field blank,
                            the email client will decide the preview text. You
                            can enforce your own preview text by filling this
                            field. Keep in mind that we cannot guarantee that
                            all email clients will show this exact text.
                          </p>
                          <img
                            style={{
                              width: '100%',

                              marginBottom: '12px'
                            }}
                            alt="example"
                            src="https://app.notificationapi.com/img/previewTextGuide.jpg"
                          />
                        </div>
                      }
                    >
                      {' '}
                      <InfoCircleTwoTone style={{ marginLeft: '3px' }} />
                    </Popover>
                  </div>
                }
              >
                <Input
                  value={preview}
                  status={previewStatus ? '' : 'error'}
                  onChange={(e) => {
                    changePreview(e.target.value);
                  }}
                />
              </FormItem>
              <FormItem
                label={
                  <span>
                    From
                    <>
                      {getEmailSenderAlert() !== undefined && (
                        <Popover
                          overlayInnerStyle={{
                            padding: 0
                          }}
                          content={
                            <div style={{ maxWidth: '400px' }}>
                              {getEmailSenderAlert()}
                            </div>
                          }
                        >
                          <Text type="warning" style={{ marginLeft: '5px' }}>
                            <WarningFilled />
                          </Text>
                        </Popover>
                      )}
                    </>
                  </span>
                }
              >
                <Input
                  value={addressFrom}
                  status={addressFromStatus ? '' : 'error'}
                  onChange={(e) => {
                    changeAddressFrom(e.target.value);
                  }}
                />
              </FormItem>
              <FormItem label={<span>From (Name)</span>}>
                <Input
                  value={nameFrom}
                  status={nameFromStatus ? '' : 'error'}
                  onChange={(e) => {
                    changeNameFrom(e.target.value);
                  }}
                />
              </FormItem>
              {(notification?.options?.EMAIL?.daily?.enabled ||
                notification?.options?.EMAIL?.hourly?.enabled ||
                notification?.options?.EMAIL?.weekly?.enabled ||
                notification?.options?.EMAIL?.monthly?.enabled) &&
              (template?.isDefaultFor.daily ||
                template?.isDefaultFor.hourly ||
                template?.isDefaultFor.weekly ||
                template?.isDefaultFor.monthly) ? (
                <BatchingInfo />
              ) : null}
            </Form>
          </Sidebar>
          <BeeContainer
            leftSidebarOpen={leftSidebarOpen}
            id="bee-plugin-container"
          />

          <Modal
            open={testing}
            onCancel={() => setTesting(false)}
            width={1000}
            title="Preview and send a test"
            okText="Send"
            okButtonProps={{
              loading: sendLoading
            }}
            onOk={async () => {
              setSendLoading(true);
              const bodyRequest: {
                subject: string;
                html: string;
                email: string[];
                previewText?: string;
              } = {
                subject:
                  (liquidResults?.result as EmailTemplateSubset).subject ?? '',
                html: (liquidResults?.result as EmailTemplateSubset).html ?? '',
                email: [testEmailAddress]
              };

              if (preview) {
                bodyRequest['previewText'] = preview;
              }

              return axiosConfig(
                'post',
                `${accountId}/emailer/test`,
                bodyRequest,
                () => {
                  setSendLoading(false);
                  message.success('Sent to your inbox.', 1);
                }
              );
            }}
          >
            <Divider />
            <Row justify="space-between" gutter={20}>
              <Col span={8}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Recipient</Text>
                  {/* antd email input */}
                  <Input
                    defaultValue={testEmailAddress}
                    onChange={(e) => {
                      setTestEmailAddress(e.target.value);
                    }}
                  />

                  <Text strong>Test Parameters</Text>

                  <TextArea
                    rows={12}
                    defaultValue={JSON.stringify(testingMergeTags, null, 2)}
                    onChange={(e) => {
                      try {
                        const json = JSON.parse(e.target.value);
                        setInvalidTestingMergeTags(false);
                        setTestingMergeTags(json);
                      } catch {
                        setInvalidTestingMergeTags(true);
                      }
                    }}
                  />

                  {invalidTestingMergeTags && (
                    <Text type="secondary">Invalid JSON</Text>
                  )}

                  {(notification?.options?.EMAIL?.hourly?.enabled &&
                    template?.isDefaultFor.hourly) ||
                  (notification?.options?.EMAIL?.daily?.enabled &&
                    template?.isDefaultFor.daily) ||
                  (notification?.options?.EMAIL?.weekly?.enabled &&
                    template?.isDefaultFor.weekly) ||
                  (notification?.options?.EMAIL?.monthly?.enabled &&
                    template?.isDefaultFor.monthly) ? (
                    <>
                      <Text strong>Batch Size</Text>
                      <InputNumber
                        min={0}
                        max={100}
                        defaultValue={batchSize}
                        onChange={(n) => {
                          setBatchSize(n ?? 0);
                        }}
                      />
                    </>
                  ) : null}
                </Space>
              </Col>

              <Col span={16}>
                <Text>Subject: </Text>
                <Text strong>
                  {(liquidResults?.result as EmailTemplateSubset).subject}
                </Text>

                <div>
                  <EmailPreviewIframe
                    src={(liquidResults?.result as EmailTemplateSubset).html}
                    scale={1}
                    clickable={true}
                  />
                </div>
              </Col>
            </Row>
          </Modal>

          <BeeSaveComponentModal
            show={showSaveModal}
            setShow={setShowSaveModal}
          />
          {savedComponentName && (
            <BeeOverwriteModal
              show={showOverwriteModal}
              setShow={setShowOverwriteModal}
            />
          )}
        </>
      )}
    </>
  );
};

export default BeeEmailEditor;
